<script setup lang="ts">
import StfCol from '@/ui/columns/StfCol.vue';
import StfChip from '@/ui/chip/StfChip.vue';
import {useProductStore} from '@/store/good';
import type Good from '@/core/model/good';
import type GoodClass from '@/core/model/good-class';
import {storeToRefs} from 'pinia';
import {computed, ref} from 'vue';
import StfIcon from '@/ui/icon/StfIcon.vue';
import StfRow from '@/ui/columns/StfRow.vue';
import useMobile from '@/composables/useMobileDependable';
import {getNumberOfGoodsToDisplay} from '@/components/goods-view/components/numberGoodDisplayed';

interface Props {
    goodClass: GoodClass
    index: number;
}

const INITIAL_GOOD_DISPLAYED_MOBILE = [6, 4, 3];
const INITIAL_GOOD_DISPLAYED_DESKTOP = [8, 6, 4];

const {isMobile} = useMobile()
const props = defineProps<Props>();
const productStore = useProductStore();
const {goods} = storeToRefs(productStore);
const showAll = ref(false);

const selectedGoodsInClass = computed(() => {
    return goods.value.filter(good => good.classNumber === props.goodClass.id).map(good => good.externalId);
})

const onChipClick = (good: Good) => {    
    productStore.toggleGood(good)
}

const toggleShowAll = () => {
    showAll.value = !showAll.value;
}

const initialGoodToDisplay = computed(() => {
    let index = props.index

    //if no option, take last
    if (index >= INITIAL_GOOD_DISPLAYED_MOBILE.length) {
        index = INITIAL_GOOD_DISPLAYED_MOBILE.length - 1;
    }

    if (isMobile.value) {
        return INITIAL_GOOD_DISPLAYED_MOBILE[index];
    }

    return INITIAL_GOOD_DISPLAYED_DESKTOP[index];
})

const suggestions = computed(() => {
    return props.goodClass.suggestions;
})

const numberOfGoodsDisplayed = computed(() => {
    // get the index of the 3rd unselected good
    let countOfUnselectedGoods = 0;
    let indexOfThirdUnselectedGood = null;

    for (let i = 0; i < suggestions.value.length; i++) {
        const good = suggestions.value[i];
        if (!selectedGoodsInClass.value.includes(good.externalId)) {
            countOfUnselectedGoods++;
        }

        if (countOfUnselectedGoods === 3) {
            indexOfThirdUnselectedGood = i;
            break;
        }
    }

    if (indexOfThirdUnselectedGood === null) {
        return suggestions.value.length;
    }

    return getNumberOfGoodsToDisplay(initialGoodToDisplay.value, indexOfThirdUnselectedGood, !isMobile.value);
})

</script>


<template>
    <StfRow vcenter class="my-2">
        <StfCol>{{ goodClass.name }}</StfCol>
        <StfCol class="has-text-right" col="4" col-desktop="2">
            <StfRow @click="toggleShowAll" hcenter vcenter class="stf-clickable">
                <StfCol narrow>
                    <StfBody class="bulma-is-display-inline-block"><u>{{ showAll ? 'Hide' : 'Show More' }}</u></StfBody>
                </StfCol>
                <StfCol narrow>
                    <StfIcon :icon="showAll ? 'chevronUp': 'chevronDown'"/>
                </StfCol>
            </StfRow>
        </StfCol>
    </StfRow>
    <StfRow>
        <StfCol v-for="(good, index) in goodClass.suggestions" v-show="index < numberOfGoodsDisplayed || showAll" :key="good.externalId" col="12" col-desktop="6">
            <StfChip full selectable @update:modelValue="onChipClick(good as Good)" :model-value="selectedGoodsInClass.includes(good.externalId)"  :text="good.name" />
        </StfCol>
    </StfRow>
</template>
